import React, { useEffect, useMemo, useState } from "react";
import { formatInTimeZone } from "date-fns-tz";
import "./Calendar.css";

export type CalendarEvent = {
  id: string;
  title: string;
  startAt: string;
  endAt: string;
};

type DayCell = {
  date: Date;
  inCurrentMonth: boolean;
};

type CalendarProps = {
  events: CalendarEvent[];
  initialMonth?: Date;
  locale?: string;
  timezone?: string;
  weekStartsOn?: "sunday" | "monday";
  className?: string;
  selectedDate?: Date | null;
  onDayClick?: (date: Date) => void;
  onVisibleMonthChange?: (month: Date) => void;
};

function getDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getEventDateKey(event: CalendarEvent, timezone: string): string | null {
  try {
    return formatInTimeZone(event.startAt, timezone, "yyyy-MM-dd");
  } catch {
    return null;
  }
}

function parseEventDate(rawDate: string): Date | null {
  const plainDatePattern = /^\d{4}-\d{2}-\d{2}$/;

  if (plainDatePattern.test(rawDate)) {
    return new Date(`${rawDate}T00:00:00`);
  }

  const parsed = new Date(rawDate);

  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed;
}

function getEventTimeRangeLabel(event: CalendarEvent, locale: string, timezone: string): string {
  const startDate = parseEventDate(event.startAt);
  const endDate = parseEventDate(event.endAt);

  if (!startDate) {
    return "--:--";
  }

  const startLabel = formatInTimeZone(startDate, timezone, "HH:mm");

  if (!endDate) {
    return startLabel;
  }

  const endLabel = formatInTimeZone(endDate, timezone, "HH:mm");

  return `${startLabel} - ${endLabel}`;
}

function buildDayCells(visibleMonth: Date, weekStartsOn: "sunday" | "monday"): DayCell[] {
  const year = visibleMonth.getFullYear();
  const month = visibleMonth.getMonth();
  const firstDayOfMonth = new Date(year, month, 1);
  const totalDaysInMonth = new Date(year, month + 1, 0).getDate();

  const firstDayWeekIndex =
    weekStartsOn === "monday"
      ? (firstDayOfMonth.getDay() + 6) % 7
      : firstDayOfMonth.getDay();
  const totalCells = Math.ceil((firstDayWeekIndex + totalDaysInMonth) / 7) * 7;

  return Array.from({ length: totalCells }, (_, index) => {
    const dayOffset = index - firstDayWeekIndex;
    const date = new Date(year, month, dayOffset + 1);

    return {
      date,
      inCurrentMonth: date.getMonth() === month
    };
  });
}

function isSameDay(first: Date, second: Date): boolean {
  return (
    first.getFullYear() === second.getFullYear() &&
    first.getMonth() === second.getMonth() &&
    first.getDate() === second.getDate()
  );
}

export default function Calendar({
  events,
  initialMonth,
  locale = "pt-BR",
  timezone = "UTC",
  weekStartsOn = "sunday",
  className,
  selectedDate,
  onDayClick,
  onVisibleMonthChange
}: CalendarProps) {
  const [visibleMonth, setVisibleMonth] = useState(() => {
    const baseDate = initialMonth ?? new Date();
    return new Date(baseDate.getFullYear(), baseDate.getMonth(), 1);
  });

  const weekdays = useMemo(() => {
    const baseWeekStartDate = weekStartsOn === "monday"
      ? new Date(2024, 0, 1)
      : new Date(2024, 0, 7);

    return Array.from({ length: 7 }, (_, index) => {
      const day = new Date(baseWeekStartDate);
      day.setDate(baseWeekStartDate.getDate() + index);

      return new Intl.DateTimeFormat(locale, {
        weekday: "short"
      }).format(day);
    });
  }, [locale, weekStartsOn]);

  const monthOptions = useMemo(() => {
    return Array.from({ length: 12 }, (_, monthIndex) => {
      const date = new Date(2024, monthIndex, 1);

      return {
        value: monthIndex,
        label: new Intl.DateTimeFormat(locale, {
          month: "long"
        }).format(date)
      };
    });
  }, [locale]);

  const monthYearSeparator = useMemo(() => {
    const formatter = new Intl.DateTimeFormat(locale, {
      month: "long",
      year: "numeric"
    });
    const parts = formatter.formatToParts(new Date(2024, 0, 1));
    const monthIndex = parts.findIndex((part) => part.type === "month");
    const yearIndex = parts.findIndex((part) => part.type === "year");

    if (monthIndex >= 0 && yearIndex >= 0 && monthIndex !== yearIndex) {
      const start = Math.min(monthIndex, yearIndex) + 1;
      const end = Math.max(monthIndex, yearIndex);
      const separator = parts
        .slice(start, end)
        .map((part) => part.value)
        .join("")
        .replace(/\s+/g, " ")
        .trim();

      if (separator) {
        return separator;
      }
    }

    const normalizedLocale = locale.toLowerCase();
    if (normalizedLocale.startsWith("pt") || normalizedLocale.startsWith("es")) {
      return "de";
    }

    return "of";
  }, [locale]);

  const yearOptions = useMemo(() => {
    const visibleYear = visibleMonth.getFullYear();
    const years = new Set<number>();

    for (let year = visibleYear - 5; year <= visibleYear + 5; year += 1) {
      years.add(year);
    }

    events.forEach((event) => {
      const dateKey = getEventDateKey(event, timezone);

      if (!dateKey) {
        return;
      }

      years.add(Number(dateKey.slice(0, 4)));
    });

    return Array.from(years).sort((a, b) => a - b);
  }, [events, visibleMonth, timezone]);

  const eventMap = useMemo(() => {
    return events.reduce<Record<string, CalendarEvent[]>>((acc, event) => {
      const dateKey = getEventDateKey(event, timezone);

      if (!dateKey) {
        return acc;
      }

      if (!acc[dateKey]) {
        acc[dateKey] = [];
      }

      acc[dateKey].push(event);
      return acc;
    }, {});
  }, [events, timezone]);

  const dayCells = useMemo(
    () => buildDayCells(visibleMonth, weekStartsOn),
    [visibleMonth, weekStartsOn]
  );

  const wrapperClassName = ["calendar", className].filter(Boolean).join(" ");
  const now = new Date();
  const isCurrentVisibleMonth =
    visibleMonth.getFullYear() === now.getFullYear() &&
    visibleMonth.getMonth() === now.getMonth();

  useEffect(() => {
    onVisibleMonthChange?.(visibleMonth);
  }, [onVisibleMonthChange, visibleMonth]);

  function goToPreviousMonth() {
    setVisibleMonth((current) =>
      new Date(current.getFullYear(), current.getMonth() - 1, 1)
    );
  }

  function goToNextMonth() {
    setVisibleMonth((current) =>
      new Date(current.getFullYear(), current.getMonth() + 1, 1)
    );
  }

  function goToCurrentMonth() {
    const now = new Date();
    setVisibleMonth(new Date(now.getFullYear(), now.getMonth(), 1));
  }

  function handleMonthChange(event: React.ChangeEvent<HTMLSelectElement>) {
    const selectedMonth = Number(event.target.value);

    if (Number.isNaN(selectedMonth)) {
      return;
    }

    setVisibleMonth((current) =>
      new Date(current.getFullYear(), selectedMonth, 1)
    );
  }

  function handleYearChange(event: React.ChangeEvent<HTMLSelectElement>) {
    const selectedYear = Number(event.target.value);

    if (Number.isNaN(selectedYear)) {
      return;
    }

    setVisibleMonth((current) =>
      new Date(selectedYear, current.getMonth(), 1)
    );
  }

  return (
    <section className={wrapperClassName}>
      <header className="calendar__header">
        <button
          type="button"
          className="calendar__nav"
          onClick={goToPreviousMonth}
          aria-label="Mes anterior"
        >
          Anterior
        </button>
        <div className="calendar__header-center">
          <div className="calendar__title-controls" role="group" aria-label="Selecionar mes e ano">
            <select
              className="calendar__selector calendar__selector--month"
              value={visibleMonth.getMonth()}
              onChange={handleMonthChange}
              aria-label="Selecionar mes"
            >
              {monthOptions.map((month) => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>

            <span className="calendar__separator" aria-hidden="true">
              {monthYearSeparator}
            </span>

            <select
              className="calendar__selector calendar__selector--year"
              value={visibleMonth.getFullYear()}
              onChange={handleYearChange}
              aria-label="Selecionar ano"
            >
              {yearOptions.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>

            {!isCurrentVisibleMonth && (
              <button
                type="button"
                className="calendar__today"
                onClick={goToCurrentMonth}
              >
                Hoje
              </button>
            )}
          </div>
        </div>
        <button
          type="button"
          className="calendar__nav"
          onClick={goToNextMonth}
          aria-label="Proximo mes"
        >
          Proximo
        </button>
      </header>

      <div className="calendar__weekdays" role="row">
        {weekdays.map((weekday, index) => (
          <div className="calendar__weekday" role="columnheader" key={`${weekday}-${index}`}>
            {weekday}
          </div>
        ))}
      </div>

      <div className="calendar__grid">
        {dayCells.map((cell) => {
          const dateKey = getDateKey(cell.date);
          const dayEvents = eventMap[dateKey] ?? [];
          const visibleEvents = dayEvents.slice(0, 2);
          const hiddenCount = dayEvents.length - visibleEvents.length;
          const isSelected = selectedDate ? isSameDay(cell.date, selectedDate) : false;
          const dayClassName = [
            "calendar__day",
            cell.inCurrentMonth ? "" : "calendar__day--muted",
            isSelected ? "calendar__day--selected" : ""
          ]
            .filter(Boolean)
            .join(" ");
          const clickable = Boolean(onDayClick);

          return (
            <article
              key={dateKey}
              className={dayClassName}
            >
              <button
                type="button"
                className={`calendar__day-button ${clickable ? "is-clickable" : ""}`.trim()}
                onClick={() => onDayClick?.(new Date(cell.date.getFullYear(), cell.date.getMonth(), cell.date.getDate()))}
              >
                <span className="calendar__day-number">{cell.date.getDate()}</span>

                <div className="calendar__events">
                  {visibleEvents.map((event) => (
                    <p key={event.id} className="calendar__event" title={event.title}>
                      {getEventTimeRangeLabel(event, locale, timezone)}
                    </p>
                  ))}

                  {hiddenCount > 0 && (
                    <p className="calendar__event-more">+{hiddenCount} evento(s)</p>
                  )}
                </div>
              </button>
            </article>
          );
        })}
      </div>
    </section>
  );
}
